import { useState } from "react";
import { attachmentService } from "@/services/attachmentService";
import { formatError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { formatDateTime } from "@/utils/formatters";
import { toast } from "sonner";
import { Upload, Download, FileText, X } from "lucide-react";

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export function AttachmentsSection({ attachments, setAttachments, canModify, user, ticketId, fileInputRef, refreshActivity }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB limit"); return; }
    setUploading(true); setUploadProgress(0);
    try {
      const { data } = await attachmentService.upload(ticketId, file, (ev) => {
        if (ev.total) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
      });
      setAttachments((prev) => [data, ...prev]);
      toast.success(`Uploaded ${file.name}`);
      await refreshActivity();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(false); setUploadProgress(0); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleDownload = async (att) => {
    try {
      const { data } = await attachmentService.download(ticketId, att.id);
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = att.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const handleDeleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.original_filename}"?`)) return;
    try {
      await attachmentService.delete(ticketId, att.id);
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
      toast.success("Attachment deleted");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <>
      {canModify && (
        <div className="space-y-2" data-testid="upload-section">
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} data-testid="file-input" />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="rounded-sm text-xs h-8 gap-1.5" data-testid="upload-btn">
            <Upload size={13} /> {uploading ? "Uploading..." : "Upload File"}
          </Button>
          {uploading && <Progress value={uploadProgress} className="h-1.5" data-testid="upload-progress" />}
          <p className="text-[10px] text-[#52525B]">Max 10 MB. Supported: PDF, images, documents, CSV, JSON</p>
        </div>
      )}
      {attachments.length === 0 && <p className="text-sm text-[#52525B] py-4 text-center" data-testid="no-attachments">No files attached</p>}
      {attachments.map((att) => (
        <Card key={att.id} className="border-[#E4E4E7] shadow-none rounded-sm" data-testid={`attachment-${att.id}`}>
          <CardContent className="p-3 flex items-center gap-3">
            <FileText size={18} className="text-[#52525B] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#09090B] truncate">{att.original_filename}</p>
              <p className="text-[10px] text-[#52525B]">{formatFileSize(att.size)} &middot; {att.uploaded_by_name} &middot; {formatDateTime(att.created_at)}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => handleDownload(att)} className="rounded-sm h-7 w-7 p-0" data-testid={`download-${att.id}`}><Download size={14} /></Button>
            {(user?.role === "admin" || user?.role === "manager") && (
              <Button variant="ghost" size="sm" onClick={() => handleDeleteAttachment(att)} className="rounded-sm h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-att-${att.id}`}><X size={14} /></Button>
            )}
          </CardContent>
        </Card>
      ))}
    </>
  );
}
